/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CheckCircleFilledIcon } from "@eds-web/react/icons";

import { CosmosText } from "@components/elements";
import { css, cx } from "@panda/css";
import { HStack } from "@panda/jsx";

type IconOptions = "check" | "dot";

interface CheckItem {
	icon?: IconOptions | null;
	label?: string | null;
}

export interface CheckListProps {
	items?: CheckItem[] | null;
}

const CheckListIcon = ({ icon }: { icon?: IconOptions }): React.JSX.Element => {
	if (icon === "dot") {
		return <span />;
	}

	return <CheckCircleFilledIcon />;
};

export const CheckListItems = ({ items = [] }: CheckListProps): React.JSX.Element | null => {
	if (!items || !items.length) return null;

	return (
		<>
			{items.map((item) => {
				if (!item.label) {
					return null;
				}

				return (
					<li
						className={cx(
							"checklist-item",
							css({
								listStyleType: item.icon !== "dot" ? "none" : "disc",
								listStylePosition: "inside",
								"& svg": {
									flexShrink: "0",
									"& path": {
										fill: "eds.text.link.primary.default",
									},
								},
								color: "eds.text.primary",
								"&::marker": {
									color: "eds.text.primary",
									fontSize: "eds.font.18",
									content: item.icon !== "dot" ? "" : '"•"',
								},
								paddingInlineStart: item.icon !== "dot" ? "0" : "eds.dimension.2",
							}),
						)}
						key={item.label}
					>
						<HStack
							alignItems="flex-start"
							display={item.icon !== "dot" ? "flex" : "inline-block"}
							gap="eds.dimension.8"
							paddingInlineStart={item.icon !== "dot" ? "0" : "eds.dimension.8"}
						>
							<CheckListIcon icon={item.icon || "check"} />
							<CosmosText>{item.label}</CosmosText>
						</HStack>
					</li>
				);
			})}
		</>
	);
};
